import { ActionTree } from 'vuex'
import {
    USER_LOAD,
    SET_USERS,
    PEER_LOAD,
    SET_PEERS,
    PEER_GRANT,
    PEER_REVOKE
} from '@/store/share/constants'
import PCrypto from '@/util/crypto/PCrypto'
import _ from 'lodash'
import ShareAPI from '@/util/api/ShareAPI'
import { ShareState, Grant } from '@/store/share/types'
import { RootState } from '@/store/types'
import { GrantRequest, GrantKey } from '@/types/api/share'
import { UserWithKey } from '@/types/user'
import { FETCH_UPDATES } from '@/store/entries/constants'
import { RESET_EDITOR } from '@/store/editor/constants'
import API from '@/util/api/API'

const actions: ActionTree<ShareState, RootState> = {
    /**
     * Load all users in the organisation
     */
    [USER_LOAD]({ commit }): void {
        API.whenConnected(async () => {
            commit(SET_USERS, await ShareAPI.users())
        })
    },

    /**
     * Load all peers with access to the current entry
     */
    async [PEER_LOAD]({ commit, rootState }): Promise<void> {
        const { entry } = rootState.editor
        const type = entry.schema === 'vault' ? 'vault' : 'login'
        commit(SET_PEERS, await ShareAPI.peers(type, entry.id!))
    },

    /**
     * Grant or update permission to the entry for a peer
     *
     * Returns true if the grant was successful
     */
    async [PEER_GRANT](
        { commit, state, rootState },
        { peer, permission }: Grant
    ): Promise<boolean> {
        const { entry } = rootState.editor
        const type = entry.schema === 'vault' ? 'vault' : 'login'
        const id = entry.id!
        const data: GrantRequest = {
            userId: peer.id,
            permission
        }
        if (type !== 'vault' && !_.find(state.peers, v => v.id === peer.id)) {
            // new peer, generate document key
            data.documentKey = PCrypto.shareDocumentKey(
                entry.documentKey!,
                peer.publicKey
            )
        } else if (type === 'vault') {
            // generate keys for all sub-entries for vaults
            data.keys = await ShareAPI.subkeys(id)
            _.each(data.keys, (key: GrantKey) => {
                key.documentKey = PCrypto.shareDocumentKey(key.documentKey, peer.publicKey)
            })
        }
        try {
            // make request and set new peer list
            commit(SET_PEERS, await ShareAPI.grant(type, id, data))
            return true
        } catch (err) {
            console.error(err)
        }
        return false
    },

    /**
     * Revoke a peer's access to the entry
     *
     * Can also be used to revoke the user's own access
     */
    async [PEER_REVOKE]({ commit, dispatch, rootState }, peer: UserWithKey): Promise<void> {
        const { entry } = rootState.editor
        const type = entry.schema === 'vault' ? 'vault' : 'login'
        const result = await ShareAPI.revoke(type, entry.id!, peer.id)
        if (!result) {
            // if you revoke yourself
            dispatch(RESET_EDITOR)
            dispatch(FETCH_UPDATES)
        } else {
            commit(SET_PEERS, result)
        }
    }
}

export default actions
